import { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUploadStore } from '@/stores/useUploadStore';
import { useOrgStore } from '@/stores/useOrgStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FileUploadStepProps {
  onNext: () => void;
}

export function FileUploadStep({ onNext }: FileUploadStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const { activeOrg } = useOrgStore();
  const { file, setFile, setUploadId, setStoragePath, setHeaders, setSample, setSuggestions, setStatus } = useUploadStore();

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    if (!activeOrg) {
      toast.error('No active organization');
      return;
    }

    if (!selectedFile.name.match(/\.(csv|xlsx?)$/i)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setFile(selectedFile);
    setLoading(true);
    setStatus('uploading');

    try {
      const uploadId = crypto.randomUUID();
      const storagePath = `${activeOrg.id}/${uploadId}/${selectedFile.name}`;

      const { error: storageError } = await supabase.storage.from('portfolio-uploads').upload(storagePath, selectedFile);
      if (storageError) throw new Error('Failed to upload file');

      setUploadId(uploadId);
      setStoragePath(storagePath);
      setStatus('parsing');

      const { data: parseResult, error: parseError } = await supabase.functions.invoke('ingest-parse', {
        body: { upload_id: uploadId, storage_path: storagePath }
      });

      if (parseError) throw new Error(parseError.message || 'Failed to parse file');

      setHeaders(parseResult.headers);
      setSample(parseResult.sample);
      setSuggestions(parseResult.suggestions || {});
      setStatus('mapped');
      
      toast.success(`File parsed: ${parseResult.row_count} rows`);
      onNext();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [activeOrg, setFile, setUploadId, setStoragePath, setHeaders, setSample, setSuggestions, setStatus, onNext]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Upload Portfolio File</h2>
        <p className="text-muted-foreground">Upload a CSV or Excel file containing your debt portfolio data</p>
      </div>

      <motion.div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        animate={{ borderColor: isDragging ? 'hsl(var(--primary))' : 'hsl(var(--border))', scale: isDragging ? 1.02 : 1 }}
        className={cn("border-2 border-dashed rounded-lg p-12 text-center transition-colors", isDragging && "bg-primary/5")}
      >
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Processing your file...</p>
          </div>
        ) : file ? (
          <div className="flex flex-col items-center gap-4">
            <FileText className="h-12 w-12 text-primary" />
            <div>
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button variant="outline" onClick={() => { setFile(null); document.getElementById('file-input')?.click(); }}>
              Change File
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <Upload className="h-12 w-12 text-muted-foreground" />
            <div>
              <p className="font-medium mb-1">Drop your file here or click to browse</p>
              <p className="text-sm text-muted-foreground">CSV or Excel files only</p>
            </div>
            <Button onClick={() => document.getElementById('file-input')?.click()}>Select File</Button>
          </div>
        )}
      </motion.div>

      <input id="file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => {
        const selected = e.target.files?.[0];
        if (selected) handleFileSelect(selected);
      }} />
    </div>
  );
}
